import React, { useRef, useEffect } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import IosFullscreenOverlayController from "./IosFullscreenOverlayController";
import { shouldShowIosFullscreenOverlay } from "../../../utilities";
import {
  selectIosFullscreenOverlayActive,
  openIosFullscreenOverlay,
  closeIosFullscreenOverlay,
} from "../../../State/Slices/userInterfaceSlice";
import { setUserInterfaceDisabled } from "../../../State/actions";

/**
 * IosFullscreenOverlay - Swipe-to-dismiss overlay for iOS Safari
 * Shows on iOS Safari landscape mode to trigger Safari UI hiding
 * Appears instantly, fades out on swipe up
 */
const IosFullscreenOverlay = () => {
  const dispatch = useDispatch();
  const controllerRef = useRef(null);
  const containerRef = useRef(null);

  const dimensionsContext = useDimensions();
  const { dimensions, symbolWidth, symbolHeight, orientation, device } =
    dimensionsContext;

  const iosFullscreenOverlayActive = useSelector(
    selectIosFullscreenOverlayActive,
  );

  // Clear dismissed flag on mount so overlay shows again on page reload
  useEffect(() => {
    sessionStorage.removeItem("iosFullscreenDismissed");
  }, []);

  // Initialize controller when container is ready
  useEffect(() => {
    if (containerRef.current && !controllerRef.current && dimensions) {
      controllerRef.current = new IosFullscreenOverlayController(
        containerRef.current,
        dispatch,
        dimensions,
        symbolWidth,
        symbolHeight,
      );
    }
  }, [dimensions, symbolWidth, symbolHeight, dispatch]);

  // Handle dimension updates
  useEffect(() => {
    if (controllerRef.current && dimensions && symbolWidth && symbolHeight) {
      controllerRef.current.updateDimensions(
        dimensions,
        symbolWidth,
        symbolHeight,
      );
    }
  }, [dimensions, symbolWidth, symbolHeight]);

  // Show/hide overlay based on orientation and device changes
  useEffect(() => {
    const isLandscape = orientation === "LANDSCAPE";
    const isMobileOrTablet = device === "MOBILE" || device === "TABLET";
    const shouldShowIosCheck = shouldShowIosFullscreenOverlay();
    const shouldShow = shouldShowIosCheck && isLandscape && isMobileOrTablet;

    if (shouldShow && !iosFullscreenOverlayActive) {
      // Show overlay when entering landscape on mobile/tablet
      dispatch(openIosFullscreenOverlay());
    } else if (
      (!isLandscape || !isMobileOrTablet) &&
      iosFullscreenOverlayActive
    ) {
      // Hide and cleanup when leaving landscape or switching to desktop
      if (controllerRef.current) {
        controllerRef.current.hide();
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }
      // Re-enable UI immediately when orientation/device changes
      dispatch(setUserInterfaceDisabled(false));
      dispatch(closeIosFullscreenOverlay());
    }
  }, [orientation, device, iosFullscreenOverlayActive, dispatch]);

  // Show/hide overlay and manage UI state
  useEffect(() => {
    if (controllerRef.current) {
      if (iosFullscreenOverlayActive) {
        // Disable UI interactions
        dispatch(setUserInterfaceDisabled(true));

        // Show overlay instantly and play animation
        controllerRef.current.show();
      } else {
        // Re-enable UI when overlay closes
        dispatch(setUserInterfaceDisabled(false));
      }
    }
  }, [iosFullscreenOverlayActive, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
      }
    };
  }, []);

  if (!dimensions || !dimensions.width || !dimensions.height) {
    return null;
  }

  return (
    <>
      {/* Spine animation container - full screen, non-interactive */}
      <Container
        name="ios_fullscreen_overlay_spine"
        x={0}
        y={0}
        sortableChildren={true}
        zIndex={iosFullscreenOverlayActive ? 20000 : -1}
        ref={containerRef}
        visible={iosFullscreenOverlayActive}
        eventMode="none"
      />
    </>
  );
};

export default IosFullscreenOverlay;
